/**
 * Typed client for Milo's HTTP API.
 *
 * Every path is relative, because the SPA is served same-origin with FastAPI in
 * production and `vite.config.ts` proxies the same paths in dev. So `/log` is
 * written identically in both, and nothing here depends on CORS.
 *
 * ## Auth is injected, not owned
 *
 * Cardea sign-in lands in its own PR. Until then `getToken` is a no-op that
 * returns null, no `Authorization` header is sent, and every authenticated call
 * fails with 401 (or 503, if the server has no tokens configured at all). That
 * is the honest state of the world, and the screens surface it as a failure
 * rather than pretending to succeed.
 *
 * When auth arrives it calls, once, at startup:
 *
 *     configureApi({ getToken: () => session.accessToken })
 *
 * `getToken` may be sync or async (so a refresh-then-retry provider drops in
 * without changing anything here) and may return null to mean "not signed in",
 * in which case the request goes out bare and the server decides.
 */

/* ── Domain vocabulary ───────────────────────────────────────────────────── */

/** Body metrics the app writes. The server accepts any string; these are ours. */
export type Metric = 'weight' | 'waist' | 'hips' | 'chest' | 'arms' | 'thighs'

/** `MealLiteral` in `src/milo/schemas.py`. */
export type Meal = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'daily_total'

/** Sources accepted by POST /log. Note it differs from the nutrition set. */
export type MeasurementSource = 'manual' | 'mfp' | 'artifact_seed' | 'fatsecret'

/** Sources accepted by POST /nutrition — `estimate` where /log has `artifact_seed`. */
export type NutritionSource = 'manual' | 'mfp' | 'estimate' | 'fatsecret'

/** `JournalTypeLiteral` in `src/milo/schemas.py`. */
export type JournalType = 'meal_feedback' | 'workout_feedback' | 'checkin' | 'note'

/** `JournalIn.sentiment`. Applies to meal and workout feedback; null elsewhere. */
export type Sentiment = 'liked' | 'disliked' | 'too_easy' | 'just_right' | 'too_hard'

/** `PlanItemIn.kind`. */
export type PlanKind = 'workout' | 'meal'

/** `PlanItemIn.status`. */
export type PlanStatus = 'planned' | 'done' | 'skipped'

/** A vital metric's slug — any slug in the registry `GET /vitals/metrics` lists,
 *  not a closed set. The four the SPA knows by name are `glucose`, `systolic`,
 *  `diastolic` and `pulse` (a blood-pressure reading is the last three as three
 *  rows, not one); anything else is whatever a device or Claude registered. The
 *  server accepts an unregistered slug and reports it under
 *  `VitalsSummaryResponse.unregistered` until someone names it. */
export type VitalMetric = string

/** `VitalMetricSpec.kind` — how the metric's rows should be read. */
export type VitalMetricKind = 'sample' | 'summary' | 'event' | 'series'

/** Where a vital row came from. The SPA only ever writes `manual`. */
export type VitalSource = 'manual' | 'eumenes' | 'dexcom_share' | 'healthkit'

/** `VitalDeviceIn.kind`. */
export type VitalDeviceKind = 'cgm' | 'glucose_meter' | 'bp_arm' | 'bp_wrist' | 'other'

/** The tables `GET /export.csv` will dump — `_EXPORT_TABLES` in `routers/read.py`. */
export type ExportTable =
  | 'measurements'
  | 'nutrition'
  | 'plan_items'
  | 'journal'
  | 'goals'
  | 'vitals'
  | 'vital_devices'
  | 'vital_metrics'
  | 'vital_metric_aliases'
  | 'vital_metric_prefs'

/** ISO-8601 calendar date, `YYYY-MM-DD`. */
export type IsoDate = string

/** ISO-8601 timestamp with offset. Only the sync clocks use this; rows carry dates. */
export type IsoDateTime = string

/* ── Request bodies ──────────────────────────────────────────────────────── */

/** One row for POST /log. Omitting `date` means the server's today in MILO_TIMEZONE. */
export interface MeasurementIn {
  date?: IsoDate
  metric: Metric
  value: number
  unit?: string
  source?: MeasurementSource
}

/** One row for POST /nutrition. Omitting `date` means the server's today. */
export interface NutritionIn {
  date?: IsoDate
  meal: Meal
  description?: string
  calories?: number
  protein_g?: number
  carbs_g?: number
  fat_g?: number
  fiber_g?: number
  source?: NutritionSource
}

/**
 * One row for POST /journal. Unlike the other two writers this one APPENDS — there is
 * no upsert key on the journal table, so posting the same content twice stores it
 * twice. Omitting `date` means the server's today.
 *
 * `ref_id` links the entry to an existing plan item and must already exist for this
 * user; the server answers 404 rather than storing a dangling reference.
 */
export interface JournalIn {
  date?: IsoDate
  type: JournalType
  content: string
  sentiment?: Sentiment | null
  ref_id?: number | null
}

/** One row for POST /goals. Upserts on (user, key), so a repeat key overwrites. */
export interface GoalIn {
  key: string
  value: string
}

/**
 * One row for POST /vitals. Idempotent on (user, metric, observed_at, source), so
 * re-posting the same reading is a no-op rather than a duplicate.
 *
 * `observed_at` is an ISO INSTANT with offset, not a bare date — a glucose curve
 * needs the minute. The browser converts its local `datetime-local` value with
 * `new Date(value).toISOString()` and sends that.
 *
 * `reading_group` is never sent by the SPA: the SERVER assigns one to a
 * systolic/diastolic/pulse triple posted together with the same `observed_at`.
 */
export interface VitalIn {
  metric: VitalMetric
  value: number
  unit?: string
  observed_at: IsoDateTime
  source?: VitalSource
  /** A device slug. An unknown slug is accepted; see `VitalDeviceIn`. */
  device?: string
  reading_group?: string
  /** CGM arrow, e.g. `flat`, `rising`. Never set by the SPA. */
  trend?: string
  note?: string
}

/** One row for POST /vitals/devices. Upserts on (user, slug). */
export interface VitalDeviceIn {
  slug: string
  label?: string
  kind?: VitalDeviceKind
  note?: string
}

/**
 * One row for POST /vitals/metrics. Upserts on `slug`. A new slug is created
 * owned by the caller; a catalogue slug takes only `registered`, `label`,
 * `range_*` and `plausible_*`, and those apply to the caller alone (a per-user
 * overlay — nothing on a catalogue row changes for anyone else); another
 * user's slug is a 403. Every field but `slug` is optional, so a bare `{slug}`
 * registers with the server's defaults and a later post only changes what it
 * names. `alias_of` makes `slug` an alias of that canonical slug instead of a
 * metric of its own, and moves any rows already stored under it. The SPA
 * never sends this; Claude does.
 */
export interface VitalMetricIn {
  slug: string
  label?: string
  unit?: string
  kind?: VitalMetricKind
  registered?: boolean
  group_key?: string
  range_min?: number
  range_max?: number
  plausible_min?: number
  plausible_max?: number
  loinc?: string
  alias_of?: string
}

/* ── Responses ───────────────────────────────────────────────────────────── */

/** Both write routes return this. Upserts, so `saved` counts rows touched. */
export interface SaveResult {
  saved: number
  ids: number[]
}

/** A point carries no `id` and no `metric` — the envelope holds the metric. */
export interface HistoryPoint {
  date: IsoDate
  value: number
  unit: string | null
  source: string
}

export interface HistoryResponse {
  metric: string
  days: number
  /** Echoed back only when the request used the range form; null otherwise. */
  from: IsoDate | null
  to: IsoDate | null
  /** One row per date, ascending, deduped across sources (manual wins). */
  points: HistoryPoint[]
}

/** Unlike a history point, a nutrition entry does carry its row id. */
export interface NutritionEntry {
  id: number
  date: IsoDate
  meal: Meal
  description: string | null
  calories: number | null
  protein_g: number | null
  carbs_g: number | null
  fat_g: number | null
  fiber_g: number | null
  source: string
}

export interface NutritionResponse {
  days: number
  from: IsoDate | null
  to: IsoDate | null
  /** The meal filter that was applied, or null for all meals. */
  meal: Meal | null
  /** One row per (date, meal), ascending. */
  entries: NutritionEntry[]
}

/** One row of the journal feed. No `user` — every read is already scoped to one. */
export interface JournalEntry {
  id: number
  date: IsoDate
  type: JournalType
  content: string
  sentiment: Sentiment | null
  /** The plan item this entry is feedback on, or null for a loose note/check-in. */
  ref_id: number | null
}

export interface JournalResponse {
  days: number
  from: IsoDate | null
  to: IsoDate | null
  /** The type filter that was applied, or null for all types. */
  type: JournalType | null
  /** NEWEST FIRST — (date desc, id desc). Not deduped; the journal is append-only. */
  entries: JournalEntry[]
}

/* Trends. Computed server-side by `src/milo/queries.py` and consumed as-is: do not
   re-derive a rolling average or a delta in the app. The MCP `get_trends` tool serves
   the identical three sections, so the coach and the screens quote the same numbers. */

export interface WeightTrendPoint {
  date: IsoDate
  /** The day's deduped reading, rounded to 1dp. */
  value: number
  /** Mean of every reading in the trailing 7 days, inclusive. Rounded to 1dp. */
  rolling_7d: number
}

/** Every field but `points` is null when the window holds no weight rows at all. */
export interface WeightTrend {
  /** Ascending by date — the opposite of the journal feed. */
  points: WeightTrendPoint[]
  current: number | null
  current_rolling_7d: number | null
  /** Change in the rolling average vs 7/30 days before the LAST reading, not before
   *  today — null when the window reaches back no further than that. */
  delta_7d: number | null
  delta_30d: number | null
}

/** Per-metric summary for every non-weight metric with data. Weight is never a key
 *  here; it has its own richer section. */
export interface MeasurementDelta {
  first: number
  latest: number
  delta_since_start: number
  delta_30d: number | null
}

/** One ISO week. Averages are over days-with-data only, so a 2-day week is not
 *  penalised — read `days_with_data` before showing an average as representative. */
export interface MacroWeek {
  /** The Monday of the week. */
  week_start: IsoDate
  days_with_data: number
  avg_calories: number | null
  avg_protein_g: number | null
  avg_carbs_g: number | null
  avg_fat_g: number | null
}

export interface TrendsResponse {
  days: number
  weight: WeightTrend
  /** Keyed by metric name — `waist`, `hips`, … Empty when nothing was logged. */
  measurements: Record<string, MeasurementDelta>
  /** Always 4 entries, newest week first, padded with nulls where no data exists.
   *  Fixed at 4 weeks server-side; `days` does not widen it. */
  macros_weekly: MacroWeek[]
}

/** A journal entry nested under the plan item it references. Same row shape as a
 *  `JournalEntry`, reached the other way round. */
export type PlanFeedback = JournalEntry

export interface PlanItem {
  id: number
  date: IsoDate
  kind: PlanKind
  title: string
  /** Free-form JSON the coach attached — sets, reps, ingredients. Shape is not fixed. */
  details: Record<string, unknown> | null
  status: PlanStatus
  /** Only entries whose `ref_id` is this item. A journal entry with no `ref_id`
   *  appears nowhere here — read `/journal` for the full feed. */
  feedback: PlanFeedback[]
}

export interface PlanResponse {
  /** Ascending by (date, id). */
  items: PlanItem[]
}

/** GET /goals — a flat key/value map, NOT a list. `{}` when nothing is set. */
export interface GoalsResponse {
  goals: Record<string, string>
}

/* The saved-meal library. A LOCAL table the coach writes over MCP (`save_meal`) —
   never synced from FatSecret — and the only place a planned meal's ingredients and
   its true numeric macros exist. `plan_items.details.macros` is prose someone typed
   and is frequently truncated; these are the numbers. */

/** One row of `saved_meals`, as `queries.saved_meal_dict` serialises it.
 *
 *  `name` is the key (`UNIQUE (user, name)` on the table), so it identifies the meal
 *  and a join may rely on an exact hit being unambiguous.
 *
 *  Every macro is nullable and so is `description`: the columns are, and a coach who
 *  never counted a meal's fibre is not a coach who counted it as zero. Nothing may
 *  substitute a 0, and nothing may render a bare `null`. */
export interface SavedMeal {
  id: number
  name: string
  /** The recipe, as free-form prose — "Chobani Zero Sugar Greek vanilla 3/4 cup
   *  (170g) + 1/2 scoop vanilla whey isolate (15g) + …". Null when never written. */
  description: string | null
  calories: number | null
  protein_g: number | null
  carbs_g: number | null
  fat_g: number | null
  fiber_g: number | null
  updated_at: IsoDateTime
}

/** GET /saved-meals — the whole library, ascending by name. */
export interface SavedMealsResponse {
  saved_meals: SavedMeal[]
}

/* Macro targets. Written by the `set_targets` MCP tool, stored effective-dated and
   SPARSE, and resolved per field by `queries.current_target` — so any one of the five
   can be null while the others are set, and a null means "never set as of this date",
   never zero. Nothing in the app may substitute a zero for one. */

/** The five macros a target can name. Order matches `queries.MACRO_FIELDS`. */
export type MacroField = 'calories' | 'protein_g' | 'carbs_g' | 'fat_g' | 'fiber_g'

/**
 * Which way a macro target points — `queries.MACRO_DIRECTION`, which is a fixed rule
 * in Python code rather than a column, and is sent over the wire precisely so this
 * file does not become a second copy of it.
 *
 * A `floor` (protein, fiber) is met by reaching it; a `ceiling` (calories, carbs, fat)
 * is met by staying under. Going past a floor is good news and past a ceiling is bad,
 * so nothing may render the two the same way.
 */
export type MacroDirection = 'floor' | 'ceiling'

/** The target in force on a date, composed from however many sparse rows contribute
 *  to it. `effective_date` is the most recent contributing row's date, NOT the date
 *  asked about, and not necessarily the date every field came from. */
export interface EffectiveTarget {
  effective_date: IsoDate
  calories: number | null
  protein_g: number | null
  carbs_g: number | null
  fat_g: number | null
  fiber_g: number | null
}

/** One macro's standing today. `remaining` is `target - consumed` and is NEGATIVE
 *  past a ceiling — it is not clamped, and reading it as a magnitude loses the miss. */
export interface MacroRemaining {
  target: number | null
  consumed: number
  remaining: number | null
}

/** GET /targets — the caller's effective macro target as of the SERVER's today. */
export interface TargetsResponse {
  /** The server's today. This screen's one reliable answer to "what day is it". */
  as_of: IsoDate
  /** Null when the user has never set a target at or before `as_of` — not an
   *  all-null target object. */
  targets: EffectiveTarget | null
  /** Null exactly when `targets` is: there is nothing to be remaining against. */
  remaining: Record<MacroField, MacroRemaining> | null
  direction: Record<MacroField, MacroDirection>
}

/** GET /admin/sync/status — FatSecret link state and the last write from each source. */
export interface SyncStatus {
  /** Whether this user has a stored FatSecret OAuth token. */
  linked: boolean
  /** Whether the server itself has FatSecret consumer credentials. False means
   *  linking cannot be attempted at all, regardless of `linked`. */
  consumer_configured: boolean
  /**
   * Does this user need to run the link ceremony again?
   *
   * NOT the same as `!linked`, and the difference is the whole reason this field
   * exists on the server rather than being derived here: the stored token row is
   * not deleted when FatSecret revokes the token upstream, so a revoked account
   * still reads as `linked: true`. The backend computes this from `linked` plus a
   * persisted `RelinkRequired` in `last_sync_error` that has not been superseded by a
   * later success — see `last_sync_error_current` — which is the only place a live
   * upstream revocation ever shows up.
   */
  relink_required: boolean
  last_fatsecret_sync: IsoDateTime | null
  /** `sync_state.status()` in `ingestion/sync_state.py` — the raw claim row behind
   *  `last_fatsecret_sync`, spread onto this response. `last_sync_ok` reads the same
   *  underlying column as `last_fatsecret_sync` and will always agree with it; both are
   *  sent because the two Settings widgets that read them were named separately before
   *  this field existed. `sync_in_progress` is what SOURCES.CFG polls after pressing
   *  "Sync now" — see `postSyncNow` — to learn when a `started` run has finished. */
  last_sync_attempt: IsoDateTime | null
  last_sync_ok: IsoDateTime | null
  last_sync_error: string | null
  sync_in_progress: boolean
  /**
   * Who recorded `last_sync_error`, and when: `"sweep"` (the 21:00 nightly timer),
   * `"on_demand"` (`POST /admin/sync/now`) or `"read"` (the MCP read-path guard).
   *
   * They exist because that column has three writers and only one row. The server now
   * clears an error only on a success from the SAME origin, so last night's sweep
   * failure survives this morning's first question instead of being silently erased by
   * it. Optional on the wire: a server older than migration 0007 does not send them.
   */
  last_sync_error_origin?: 'sweep' | 'on_demand' | 'read' | null
  last_sync_error_at?: IsoDateTime | null
  /**
   * Is `last_sync_error` still describing the world, or has a later success superseded
   * it?
   *
   * The direct consequence of that origin-conditional clearing: an error from one
   * origin outlives a success from another, which is right for diagnosing the sweep and
   * wrong for anything that asks "did MY sync work". Press "Sync now" the morning after
   * a failed sweep and the sweep's error is still on the record — so this, not
   * `last_sync_error`, is what tells the button whether to report a failure.
   *
   * Absent from an older server; callers treat `undefined` as "current", which is the
   * pre-0007 behaviour.
   */
  last_sync_error_current?: boolean
  /** Uncovered units (food days / weight months) awaiting a retry, and the oldest few
   *  of them. Zero is what "coverage is complete" means. */
  open_gaps?: number
  open_gap_units?: SyncGapUnit[]
}

/** One uncovered unit from `open_gap_units`. `unit_key` is an ISO date for a
 *  `food_day` and `YYYY-MM` for a `weight_month`. */
export interface SyncGapUnit {
  unit_kind: 'food_day' | 'weight_month'
  unit_key: string
  attempts: number
  first_seen_at: IsoDateTime
  last_error: string | null
}

/**
 * POST /admin/sync/now — the two outcomes that come back as a 2xx. `recently_synced`
 * means the debounce fired and FatSecret was never called; `started` means a
 * background fetch is now running and `sync_in_progress` (via `getSyncStatus`) is how
 * a caller learns it finished.
 *
 * The other three outcomes the endpoint can produce — `sync_in_progress` (409),
 * `relink_required` (409) and "not configured" (503) — are not part of this type
 * because they never reach a caller as a resolved value: `request` throws them as an
 * `ApiError`, distinguished by `.status` and `.detail`. See `postSyncNow`.
 */
export type SyncNowResult =
  | { status: 'recently_synced'; last_sync_ok: IsoDateTime; debounce_seconds: number }
  | { status: 'started'; from: IsoDate; to: IsoDate }

/**
 * POST /admin/fatsecret/link/start — where to send the browser to authorize.
 *
 * There is deliberately no `complete` counterpart: the browser finishes the ceremony by
 * NAVIGATING to `/auth/fatsecret/callback`, which FatSecret redirects it to. Nothing in
 * this client ever calls that route.
 */
export interface FatSecretLinkStart {
  /** FatSecret's authorize page, carrying the request token. */
  authorize_url: string
  /** Seconds the pending attempt (and its correlation cookie) stay valid. */
  expires_in: number
}

/** GET /export.csv — a file, not JSON. See `fetchExportCsv`. */
export interface CsvExport {
  blob: Blob
  /** From the server's Content-Disposition, falling back to `<table>.csv`. */
  filename: string
}

/** One stored vital, as `GET /vitals` and the write echo return it. */
export interface VitalRow {
  id: number
  user: string
  /** The canonical slug — an alias sent on the write is resolved before storage. */
  metric: VitalMetric
  /** Whether `metric` is in the registry. An unregistered row is stored and
   *  served like any other; it is simply not yet named. */
  registered: boolean
  /** The registry's human label for `metric`; falls back to the slug itself
   *  when the metric is unregistered. */
  label: string
  /** Only on a write echo, and only when the slug sent was an alias: the slug
   *  as it arrived, before it resolved to `metric`. */
  metric_as_sent?: string
  value: number
  unit: string
  observed_at: IsoDateTime
  source: VitalSource
  /** The device slug, or null for a reading with no device. */
  device: string | null
  /** The device's human label, joined by the server; null when there is no
   *  device or the slug was never registered. Fall back to `device`. */
  device_label: string | null
  /** Ties a systolic/diastolic/pulse triple together. Null for a lone row. */
  reading_group: string | null
  trend: string | null
  note: string | null
  /** The app that wrote the sample upstream, when the source knew it (Health
   *  Auto Export relays HealthKit's per-sample source: `Oura`, `Omron connect`).
   *  Null when the writer did not have it. */
  origin: string | null
  /** Cross-source resolution (issue #128): null for a row that is shown; else
   *  the id of the row that displaces it — the same reading arrived through two
   *  routes and the higher-priority source won. `GET /vitals` leaves such rows
   *  out unless `include_shadowed` is set; the summaries never count them. The
   *  screen draws what it is given and never re-derives this. */
  shadowed_by: number | null
}

export interface VitalDevice {
  id: number
  slug: string
  label: string
  kind: VitalDeviceKind
  note: string | null
}

export interface VitalsResponse {
  /** Newest first. */
  rows: VitalRow[]
  /** The server's row cap (2000). `truncated` says whether it was hit. */
  limit: number
  truncated: boolean
  /** The window actually read, after the server filled in the defaults. */
  from: IsoDateTime
  to: IsoDateTime
  metric: VitalMetric | null
  device: string | null
  /** Whether rows of unregistered metrics were included in the read. */
  include_unregistered: boolean
  /** Whether rows a cross-source duplicate displaced were included. */
  include_shadowed: boolean
}

/** One metric's figures over a window. `time_in_range_pct` is present on any
 *  metric whose registry row carries a range (glucose ships with one), and is
 *  the server's number — render it, never re-derive it. */
export interface VitalMetricSummary {
  n: number
  min: number
  max: number
  mean: number
  last: number
  last_at: IsoDateTime
  time_in_range_pct?: number
}

/** One day of the summary window: `date` plus one key per metric in
 *  `VitalsSummaryResponse.metrics`, null on a day that metric has no row. */
export type VitalsDaySummary = { date: IsoDate } & Record<string, VitalMetricSummary | null>

/** One entry of `VitalsSummaryResponse.metrics` — the registered metrics with
 *  rows in the window, in catalogue order, each with the range its
 *  `time_in_range_pct` was computed against. */
export interface VitalsSummaryMetric {
  slug: string
  label: string
  unit: string
  kind: VitalMetricKind
  range: [number, number] | null
}

/** The most recent reading of one metric WITHIN the summary window; null when the
 *  metric has no reading in it, even if one exists further back. */
export interface VitalLatest {
  value: number
  unit: string
  observed_at: IsoDateTime
  device_label: string | null
  source: VitalSource
}

/**
 * GET /vitals/summary. `range_mgdl` is glucose's registry range, the band the
 * server computed its `time_in_range_pct` against — the SPA draws the band it
 * is GIVEN and does not know the numbers itself; null once the range is
 * cleared, in which case no band is drawn. `metrics` lists the registered
 * metrics that HAVE rows in the window, in catalogue order, and those slugs are
 * exactly the keys of `window`, `latest` and each `days_list` entry: a metric
 * with no rows in the window is absent, never a permanent null. Rows of
 * unregistered slugs are counted in `unregistered` (slug → rows) and
 * `unregistered_slugs`, which is 0 rather than omitted when there are none.
 */
export interface VitalsSummaryResponse {
  days: number
  range_mgdl: [number, number] | null
  metrics: VitalsSummaryMetric[]
  days_list: VitalsDaySummary[]
  window: Record<string, VitalMetricSummary>
  latest: Record<string, VitalLatest>
  unregistered_slugs: number
  unregistered: Record<string, number>
  /** The cross-source resolver's decisions made in the window, by outcome
   *  (issue #128). Rows it shadowed are in none of the figures above. */
  resolutions: { shadowed: number; near_misses: number; kind_mismatches: number }
}

/** One registry row as `GET /vitals/metrics` lists it FOR THE CALLER — the
 *  catalogue plus the caller's own rows, with the caller's per-user overrides
 *  (`registered`, `label`, `range`, plausible bounds) applied — with its row tally. */
export interface VitalMetricSpec {
  id: number
  slug: string
  label: string
  unit: string
  kind: VitalMetricKind
  value_type: 'number' | 'text' | 'json'
  /** Metrics that read together (a BP triple), or null. */
  group_key: string | null
  /** True only for a row a migration seeded; false for anything a person or an
   *  arrival created. Never settable through the API. */
  canonical: boolean
  registered: boolean
  range: [number, number] | null
  plausible_min: number | null
  plausible_max: number | null
  loinc: string | null
  /** Null for a catalogue row; otherwise the user who registered it (always the
   *  caller — another user's rows are not listed). */
  owner: string | null
  /** Rows stored under this slug, all time. */
  n_rows: number
  first_at: IsoDateTime | null
  last_at: IsoDateTime | null
}

export interface VitalMetricAlias {
  alias: string
  canonical_slug: string
}

export interface VitalMetricsResponse {
  metrics: VitalMetricSpec[]
  aliases: VitalMetricAlias[]
}

/** POST /vitals/metrics echoes the registry rows it touched. An alias entry
 *  also says how many existing rows were `moved` under the canonical slug. */
export interface VitalMetricSaveResult {
  saved: number
  metrics: VitalMetricSpec[]
  aliases: (VitalMetricAlias & { moved: number })[]
}

/** POST /vitals echoes the rows it stored, `reading_group` filled in. */
export interface VitalsSaveResult {
  saved: number
  ids: number[]
  rows: VitalRow[]
}

export interface VitalDevicesResponse {
  devices: VitalDevice[]
}

export interface VitalDeviceSaveResult {
  saved: number
  ids: number[]
  devices: VitalDevice[]
}

/* ── Query parameters ────────────────────────────────────────────────────── */

/**
 * `from`/`to` and `days` are alternatives: supply either and the range form
 * wins, supply neither and the server looks back `days` from its today.
 */
export interface HistoryQuery {
  metric: Metric
  /** Lookback window when `from`/`to` are absent. Server default 90. */
  days?: number
  from?: IsoDate
  to?: IsoDate
}

export interface NutritionQuery {
  /** Lookback window when `from`/`to` are absent. Server default 14. */
  days?: number
  from?: IsoDate
  to?: IsoDate
  meal?: Meal
}

export interface JournalQuery {
  /** Lookback window when `from`/`to` are absent. Server default 90. */
  days?: number
  from?: IsoDate
  to?: IsoDate
  type?: JournalType
}

export interface TrendsQuery {
  /** Window for the weight series and the measurement deltas. Server default 90.
   *  Does NOT affect `macros_weekly`, which is always the last 4 weeks. */
  days?: number
}

/**
 * `/plan` takes a plain window and has no `days` form — it defaults to today-7
 * through today+7, so it is the one read that looks into the future.
 */
export interface PlanQuery {
  from?: IsoDate
  to?: IsoDate
}

/**
 * `/saved-meals` takes one optional filter and it is an EXACT name match, not a
 * search: the server compares `saved_meals.name == name.strip()`. A partial name
 * matches nothing, and an unknown one is an empty list rather than a 404.
 */
export interface SavedMealsQuery {
  name?: string
}

/**
 * `/vitals` takes an instant window, not dates: `to` defaults to the server's
 * now and `from` to one day before `to`. Both are ISO instants with offset, so a
 * browser asking for "today" sends its own local midnight as a UTC string and
 * the server does no day-boundary guessing of its own.
 */
export interface VitalsQuery {
  from?: IsoDateTime
  to?: IsoDateTime
  metric?: VitalMetric
  device?: string
  /** Whether rows of unregistered slugs are included. Omit for the server's default. */
  include_unregistered?: boolean
  /** Whether rows a cross-source duplicate displaced are included. Omit for
   *  the server's default, which is to hide them (one row per reading). */
  include_shadowed?: boolean
}

export interface VitalsSummaryQuery {
  /** Lookback window. Server default 14. */
  days?: number
}

export interface VitalMetricsQuery {
  /** Whether unregistered slugs (rows exist, no registry row) are listed too.
   *  Omit for the server's default, which is to include them. */
  include_unregistered?: boolean
}

/* ── Errors ──────────────────────────────────────────────────────────────── */

/** One entry of FastAPI's stock 422 body. */
export interface ValidationItem {
  type: string
  loc: (string | number)[]
  msg: string
  input?: unknown
  url?: string
}

/**
 * `detail` is a list for Pydantic validation failures and a bare string for the
 * handful of hand-raised 422s (`from must be <= to`) and for every 401/404/503.
 * Callers must handle both.
 */
export type ErrorDetail = string | ValidationItem[]

/** The server answered, but not with success. */
export class ApiError extends Error {
  readonly status: number
  readonly detail: ErrorDetail | null

  constructor(status: number, detail: ErrorDetail | null, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }

  /** 401 (no/!invalid token) and 503 (server has no tokens configured) both mean
   *  "this will never work until auth lands", and the UI says so the same way. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403 || this.status === 503
  }
}

/** The request never got an answer — offline, DNS, CORS, dev proxy down. */
export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options)
    this.name = 'NetworkError'
  }
}

/** A short, human sentence for any thrown value. Safe to render directly. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isAuthFailure) return 'Not signed in — sign-in lands in the auth PR.'
    return error.message
  }
  if (error instanceof NetworkError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

/* ── Configuration ───────────────────────────────────────────────────────── */

/** Returns the bearer token, or null when nobody is signed in. */
export type TokenProvider = () => string | null | Promise<string | null>

export interface ApiOptions {
  /** Supplied by the auth PR. Defaults to a no-op returning null. */
  getToken?: TokenProvider
  /** Prefix for every path. Defaults to '' (same-origin). Mostly for tests. */
  baseUrl?: string
}

const NO_TOKEN: TokenProvider = () => null

let getToken: TokenProvider = NO_TOKEN
let baseUrl = ''

/** Merge options into the module-level client. Call once at startup. */
export function configureApi(options: ApiOptions): void {
  if (options.getToken !== undefined) getToken = options.getToken
  if (options.baseUrl !== undefined) baseUrl = options.baseUrl
}

/** Drop back to the unauthenticated defaults. For tests and sign-out. */
export function resetApi(): void {
  getToken = NO_TOKEN
  baseUrl = ''
}

/* ── Transport ───────────────────────────────────────────────────────────── */

/** Narrow an unknown parsed body to the `{detail}` envelope, without casting. */
function readDetail(body: unknown): ErrorDetail | null {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return null
  const detail: unknown = (body as { detail: unknown }).detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // Keep only entries that look like validation items; anything else is noise.
    // `loc` is checked as well as `msg` because `describe` reads both: FastAPI
    // always sends it, but a proxy or a custom handler in front of it need not,
    // and a missing `loc` would throw a TypeError out of `describe` before the
    // ApiError was ever built — the caller would see an internal error instead
    // of the server's. The parser owns the shape contract; describe trusts it.
    const items = detail.filter(
      (item): item is ValidationItem =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as ValidationItem).msg === 'string' &&
        Array.isArray((item as ValidationItem).loc),
    )
    return items.length > 0 ? items : null
  }
  return null
}

function describe(status: number, detail: ErrorDetail | null): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // "value: Input should be a valid number" — the field, then the complaint.
    return detail
      .map((item) => {
        const field = item.loc.filter((part) => part !== 'body').join('.')
        return field ? `${field}: ${item.msg}` : item.msg
      })
      .join('; ')
  }
  return `Request failed (${status}).`
}

type QueryValues = Record<string, string | number | undefined>

/** A boolean query flag as FastAPI reads it — `'true'`/`'false'` — or omitted. */
function flag(value: boolean | undefined): string | undefined {
  return value === undefined ? undefined : value ? 'true' : 'false'
}

function buildUrl(path: string, query?: QueryValues): string {
  let url = `${baseUrl}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, String(value))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }
  return url
}

/**
 * `Accept` plus the bearer, for both the JSON and the CSV path.
 *
 * The CSV route needs this as much as the JSON ones do: `/export.csv` is gated on
 * `milo:read` like every other read, so a plain `<a href="/export.csv">` carries no
 * Authorization header and comes back 401. The only way to download it is to fetch it
 * with the token and hand the browser a Blob — which is what `fetchExportCsv` does.
 */
async function buildHeaders(accept: string): Promise<Record<string, string>> {
  // A provider that throws must not look like a network failure, so this is
  // awaited by callers outside their fetch try-block on purpose.
  const headers: Record<string, string> = { Accept: accept }
  const token = await getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (cause) {
    // AbortError is a caller-initiated cancel, not a failure. Let it through.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError('Could not reach the server.', { cause })
  }
}

/** Build the ApiError for a non-2xx. `parsed` is the body if it was JSON, else null. */
function failure(status: number, parsed: unknown): ApiError {
  const detail = readDetail(parsed)
  return new ApiError(status, detail, describe(status, detail))
}

/** An error body is not guaranteed to be JSON — a proxy in front of a dead upstream
 *  serves HTML — so parsing is always allowed to fail. */
function parseJson(text: string): unknown {
  if (!text) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

interface RequestOptions {
  method: 'GET' | 'POST'
  path: string
  query?: QueryValues
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(options: RequestOptions): Promise<T> {
  const { method, path, query, body, signal } = options

  const url = buildUrl(path, query)
  const headers = await buildHeaders('application/json')
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await send(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  const parsed = parseJson(await response.text())
  if (!response.ok) throw failure(response.status, parsed)

  // The success shapes are contract, not guesses; the server is the authority
  // on them and a mismatch is a backend bug rather than something to re-check
  // on every field at runtime.
  return parsed as T
}

/** `Content-Disposition: attachment; filename="journal.csv"` → `journal.csv`. */
function filenameFrom(header: string | null, fallback: string): string {
  if (!header) return fallback
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  return match ? decodeURIComponent(match[1]) : fallback
}

/* ── Endpoints ───────────────────────────────────────────────────────────── */

/**
 * POST /log — one measurement or many. Upserts on (user, date, metric, source),
 * so re-saving the same metric for the same day overwrites rather than appends.
 * Send a whole tape session as one array: one request, one transaction.
 */
export function postLog(
  body: MeasurementIn | MeasurementIn[],
  signal?: AbortSignal,
): Promise<SaveResult> {
  return request<SaveResult>({ method: 'POST', path: '/log', body, signal })
}

/**
 * POST /nutrition — one entry or many. Upserts on (user, date, meal, source):
 * a second breakfast on the same day replaces the first.
 */
export function postNutrition(
  body: NutritionIn | NutritionIn[],
  signal?: AbortSignal,
): Promise<SaveResult> {
  return request<SaveResult>({ method: 'POST', path: '/nutrition', body, signal })
}

/** GET /history — one metric's deduped series. */
export function getHistory(
  query: HistoryQuery,
  signal?: AbortSignal,
): Promise<HistoryResponse> {
  return request<HistoryResponse>({
    method: 'GET',
    path: '/history',
    query: { metric: query.metric, days: query.days, from: query.from, to: query.to },
    signal,
  })
}

/** GET /nutrition — deduped entries over a window. */
export function getNutrition(
  query: NutritionQuery = {},
  signal?: AbortSignal,
): Promise<NutritionResponse> {
  return request<NutritionResponse>({
    method: 'GET',
    path: '/nutrition',
    query: { days: query.days, from: query.from, to: query.to, meal: query.meal },
    signal,
  })
}

/**
 * POST /journal — one entry or many. APPENDS; there is no upsert key on this table,
 * so submitting twice stores two rows. Guard double-submits in the UI.
 */
export function postJournal(
  body: JournalIn | JournalIn[],
  signal?: AbortSignal,
): Promise<SaveResult> {
  return request<SaveResult>({ method: 'POST', path: '/journal', body, signal })
}

/** GET /journal — the flat feed, newest first. */
export function getJournal(
  query: JournalQuery = {},
  signal?: AbortSignal,
): Promise<JournalResponse> {
  return request<JournalResponse>({
    method: 'GET',
    path: '/journal',
    query: { days: query.days, from: query.from, to: query.to, type: query.type },
    signal,
  })
}

/**
 * GET /trends — the server's computed weight trend, measurement deltas and weekly
 * macro averages. Render these numbers; do not recompute them from `/history` or
 * `/nutrition`, or the app and the coach will disagree about the same week.
 */
export function getTrends(
  query: TrendsQuery = {},
  signal?: AbortSignal,
): Promise<TrendsResponse> {
  return request<TrendsResponse>({
    method: 'GET',
    path: '/trends',
    query: { days: query.days },
    signal,
  })
}

/** GET /plan — plan items with their linked feedback. Defaults to a 15-day window
 *  centred on today, so it returns items dated in the future. */
export function getPlan(query: PlanQuery = {}, signal?: AbortSignal): Promise<PlanResponse> {
  // The wire names are `from`/`to`; the Python parameters behind them are
  // `from_date`/`to_date` (aliased) — a mismatch that has already cost time once.
  return request<PlanResponse>({
    method: 'GET',
    path: '/plan',
    query: { from: query.from, to: query.to },
    signal,
  })
}

/** GET /goals — every goal as one flat map. */
export function getGoals(signal?: AbortSignal): Promise<GoalsResponse> {
  return request<GoalsResponse>({ method: 'GET', path: '/goals', signal })
}

/** POST /goals — one goal or many. Upserts on (user, key). Returns save counts, NOT
 *  the resulting map; call `getGoals` after if the screen needs to re-read. */
export function postGoals(body: GoalIn | GoalIn[], signal?: AbortSignal): Promise<SaveResult> {
  return request<SaveResult>({ method: 'POST', path: '/goals', body, signal })
}

/**
 * GET /targets — the macro target actually in force, as of the server's today.
 *
 * No parameters, deliberately: the route dates itself from `config.today()`, which is
 * the only clock this app is allowed to believe. There is no way to ask for another
 * day's target, and MACROS must not want one — its kcal axis is a fixed scale so that
 * days compare to each other, which a per-day target would destroy.
 *
 * A macro is null when nothing has ever set it, and the whole `targets` object is null
 * when nothing has ever set anything. Neither is a zero, and neither may be divided by.
 */
export function getTargets(signal?: AbortSignal): Promise<TargetsResponse> {
  return request<TargetsResponse>({ method: 'GET', path: '/targets', signal })
}

/**
 * GET /saved-meals — the caller's saved-meal library, ascending by name.
 *
 * The same payload the coach's `get_saved_meals` MCP tool returns, from the same query
 * function: this is how the app joins a planned meal to its recipe and its real macros
 * without either surface computing a second answer.
 *
 * `name` filters to the one meal with exactly that name. An unknown name is a 200 with
 * an empty list, NOT a 404 — a plan item can name a meal that has since been renamed,
 * and the join has to tell that from a broken endpoint.
 *
 * No paging parameter exists. The table is human-curated and tens of rows deep, and the
 * route deliberately has no limit for the library to be silently truncated by.
 */
export function getSavedMeals(
  query: SavedMealsQuery = {},
  signal?: AbortSignal,
): Promise<SavedMealsResponse> {
  return request<SavedMealsResponse>({
    method: 'GET',
    path: '/saved-meals',
    query: { name: query.name },
    signal,
  })
}

/**
 * GET /vitals — raw readings over an instant window, newest first, capped at the
 * server's `limit`. A CGM writes ~288 glucose rows a day, so one day of every
 * metric fits comfortably; a longer window should ask per metric.
 */
export function getVitals(
  query: VitalsQuery = {},
  signal?: AbortSignal,
): Promise<VitalsResponse> {
  return request<VitalsResponse>({
    method: 'GET',
    path: '/vitals',
    query: {
      from: query.from,
      to: query.to,
      metric: query.metric,
      device: query.device,
      include_unregistered: flag(query.include_unregistered),
      include_shadowed: flag(query.include_shadowed),
    },
    signal,
  })
}

/**
 * GET /vitals/summary — per-day and whole-window figures, computed by the server.
 * Render them; the time-in-range percentage in particular is never re-derived
 * from `/vitals` rows, or the app and the coach would quote different numbers.
 */
export function getVitalsSummary(
  query: VitalsSummaryQuery = {},
  signal?: AbortSignal,
): Promise<VitalsSummaryResponse> {
  return request<VitalsSummaryResponse>({
    method: 'GET',
    path: '/vitals/summary',
    query: { days: query.days },
    signal,
  })
}

/**
 * POST /vitals — one reading or many. Idempotent on (user, metric, observed_at,
 * source). Send a blood-pressure triple as ONE array with ONE `observed_at`: the
 * server groups them under a `reading_group` the client never chooses.
 */
export function postVitals(
  body: VitalIn | VitalIn[],
  signal?: AbortSignal,
): Promise<VitalsSaveResult> {
  return request<VitalsSaveResult>({ method: 'POST', path: '/vitals', body, signal })
}

/** GET /vitals/devices — the caller's registered devices. */
export function getVitalDevices(signal?: AbortSignal): Promise<VitalDevicesResponse> {
  return request<VitalDevicesResponse>({ method: 'GET', path: '/vitals/devices', signal })
}

/** POST /vitals/devices — register or relabel a device. Upserts on (user, slug). */
export function postVitalDevice(
  body: VitalDeviceIn | VitalDeviceIn[],
  signal?: AbortSignal,
): Promise<VitalDeviceSaveResult> {
  return request<VitalDeviceSaveResult>({
    method: 'POST',
    path: '/vitals/devices',
    body,
    signal,
  })
}

/** GET /vitals/metrics — the metric registry, aliases included, with a row
 *  tally per slug. Unregistered slugs are listed too unless told otherwise. */
export function getVitalMetrics(
  query: VitalMetricsQuery = {},
  signal?: AbortSignal,
): Promise<VitalMetricsResponse> {
  return request<VitalMetricsResponse>({
    method: 'GET',
    path: '/vitals/metrics',
    query: { include_unregistered: flag(query.include_unregistered) },
    signal,
  })
}

/** POST /vitals/metrics — register, relabel or alias a metric. Upserts on
 *  (user, slug); an `alias_of` entry moves the alias's existing rows too. */
export function postVitalMetric(
  body: VitalMetricIn | VitalMetricIn[],
  signal?: AbortSignal,
): Promise<VitalMetricSaveResult> {
  return request<VitalMetricSaveResult>({
    method: 'POST',
    path: '/vitals/metrics',
    body,
    signal,
  })
}

/** GET /admin/sync/status — FatSecret link and last-sync clocks. Read-only. */
export function getSyncStatus(signal?: AbortSignal): Promise<SyncStatus> {
  return request<SyncStatus>({ method: 'GET', path: '/admin/sync/status', signal })
}

/**
 * POST /admin/sync/now — the manual "Sync now" trigger (`routers/admin.py`). Pulls
 * today + yesterday, debounced 90s on `last_sync_ok`, and always answers
 * synchronously and honestly:
 *
 *   200 `recently_synced` — inside the debounce; FatSecret was not called.
 *   202 `started`         — a background fetch is running now.
 *   409 `sync_in_progress`— someone else's sync holds the claim.
 *   409 `relink_required` — the stored FatSecret token is gone or won't decrypt.
 *   503                   — the server has no FatSecret consumer credentials.
 *
 * The first two resolve this promise as a `SyncNowResult`; the last three throw an
 * `ApiError` whose `.status`/`.detail` a caller must switch on individually — `.detail`
 * is the bare string `"sync_in_progress"` / `"relink_required"` for the two 409s, so
 * `error.message` alone cannot tell them apart from each other or from a 503, and
 * every one of the five needs its own on-screen outcome (see SOURCES.CFG in
 * `screens/Settings.tsx`).
 *
 * KNOWN GAP, not fixed here (frontend-only change; see the PR body): this debounce and
 * the read-path freshness guard (`freshness.ensure_fresh`, 15s TTL, today-only) share
 * one `last_sync_ok` column but cover different windows. A read-path pull can stamp
 * `last_sync_ok`, and a press of this button within 90s of that stamp then answers
 * `recently_synced` without ever fetching yesterday. That is a real gap in coverage,
 * not a client bug — `recently_synced` is reported exactly as honestly as the backend
 * knows how to report it.
 */
export function postSyncNow(signal?: AbortSignal): Promise<SyncNowResult> {
  return request<SyncNowResult>({ method: 'POST', path: '/admin/sync/now', signal })
}

/**
 * POST /admin/fatsecret/link/start — begin the browser FatSecret link ceremony.
 *
 * Takes NO body and acts only on the calling principal: the route has no field with
 * which a caller could name a different user, deliberately, so this cannot become an
 * admin-over-others action.
 *
 * Two things the caller must know:
 *
 * 1. **The response sets an `HttpOnly` correlation cookie**, scoped to
 *    `/auth/fatsecret/callback`. It is what binds the ceremony to THIS browser, and
 *    without it the callback refuses to link — which is what stops one user phishing
 *    another into authorizing an attempt bound to the first user's account. Nothing in
 *    JS can read or forward it, and nothing needs to.
 * 2. **Navigate the CURRENT tab to `authorize_url`, with `location.assign`.** Not
 *    `window.open`: the URL is only known after awaiting this call, so the open is not
 *    synchronous with the user's click and iOS Safari blocks it as an unsolicited popup
 *    — on exactly the phone browser this feature exists to serve, with no error the user
 *    could report.
 *
 * Throws an `ApiError` on 503 (the server has no FatSecret consumer credentials, or no
 * usable callback URL configured) and on 502 (FatSecret refused the request token, or
 * did not confirm the callback). Neither ever carries a FatSecret response body.
 */
export function postFatSecretLinkStart(signal?: AbortSignal): Promise<FatSecretLinkStart> {
  return request<FatSecretLinkStart>({
    method: 'POST',
    path: '/admin/fatsecret/link/start',
    signal,
  })
}

/**
 * GET /export.csv — the raw table dump, as a Blob plus its filename.
 *
 * Not a URL helper, deliberately. The route is gated on `milo:read`, and neither an
 * `<a href>` nor a `window.open` sends the bearer token, so both come back 401 — the
 * token has to be attached, which means fetching it here. Callers turn the Blob into
 * a download with `URL.createObjectURL`, and must call `URL.revokeObjectURL` after.
 *
 * The rows are the RAW table, not the deduped view the other reads return: an export
 * of `measurements` includes every source's row for a day, not just the winner.
 */
export async function fetchExportCsv(
  table: ExportTable,
  signal?: AbortSignal,
): Promise<CsvExport> {
  const url = buildUrl('/export.csv', { table })
  const headers = await buildHeaders('text/csv')
  const response = await send(url, { method: 'GET', headers, signal })

  // Success is CSV, but a failure is still FastAPI's JSON `{detail}` envelope — so
  // the body is only read as text-then-JSON on the error path, and the success path
  // never buffers the file as a string.
  if (!response.ok) throw failure(response.status, parseJson(await response.text()))

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition'), `${table}.csv`),
  }
}
